import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { 
  getAssets, 
  createAsset, 
  updateAsset, 
  deleteAsset,
  restoreAsset,
  getAddHardwareFormData,
  getEditHardwareData,
  importAssets,
  allocateAsset,
  sendDeclarationEmail
} from '../../../models/assetsModel';
import AddHardwareModal from './modals/AddHardwareModal';
import EditHardwareModal from './modals/EditHardwareModal';
import ViewHardwareModal from './modals/ViewHardwareModal';
import AllocateAssetModal from './modals/AllocateAssetModal';
import ImportAssetsModal from './modals/ImportAssetsModal';
import AssetTagModal from './modals/AssetTagModal';

// Debounced Input for Search
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, onChange, debounce]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}

function HardwareAssets() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialPagination = () => {
    const searchParams = new URLSearchParams(location.search);
    const page = parseInt(searchParams.get('page')) || 1;
    const pageSize = parseInt(searchParams.get('pageSize')) || 20;
    return { pageIndex: page - 1, pageSize };
  };

  const [pagination, setPagination] = useState(initialPagination);
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  
  // Load view mode from localStorage, default to 'grid'
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('hardwareAssetsViewMode');
    return savedViewMode || 'grid';
  });

  // Save view mode to localStorage whenever it changes
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('hardwareAssetsViewMode', mode);
  };
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [resendingEmail, setResendingEmail] = useState({});

  const fetchAssets = useCallback(async (searchTerm = globalFilter, sortConfig = sorting) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.pageIndex + 1);
      params.append('pageSize', pagination.pageSize);
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (sortConfig.length > 0) {
        params.append('sortBy', sortConfig[0].id);
        params.append('sortOrder', sortConfig[0].desc ? 'DESC' : 'ASC');
      }
       
      const response = await getAssets(params);
      
      if (response.status === 1 && response.data) {
        // Handle nested data structure: response.data.data.rows
        const nestedData = response.data.data || response.data;
        const dataRows = nestedData.rows || nestedData;
        setAssets(Array.isArray(dataRows) ? dataRows : []);
        setRowCount(nestedData.total || (Array.isArray(dataRows) ? dataRows.length : 0));
      } else {
        toast.error(response.message || 'Failed to load hardware assets');
        setAssets([]);
        setRowCount(0);
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while loading hardware assets');
      setAssets([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Debounced search handler
  const handleSearch = useCallback((searchTerm) => {
    setGlobalFilter(searchTerm);
    // Reset to first page when searching
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Sorting handler
  const handleSort = useCallback((columnId) => {
    setSorting(prev => {
      const existingSort = prev.find(sort => sort.id === columnId);
      if (existingSort) {
        // Toggle existing sort
        return [{ id: columnId, desc: !existingSort.desc }];
      } else {
        // Add new sort
        return [{ id: columnId, desc: false }];
      }
    });
  }, []);

  // Effect to fetch data when search or sorting changes
  useEffect(() => {
    fetchAssets(globalFilter, sorting);
  }, [globalFilter, sorting, fetchAssets]);

  // Effect to update URL (debounced to prevent excessive updates)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('page', pagination.pageIndex + 1);
      searchParams.set('pageSize', pagination.pageSize);
      
      if (globalFilter) {
        searchParams.set('search', globalFilter);
      } else {
        searchParams.delete('search');
      }
      
      if (sorting.length > 0) {
        searchParams.set('sortBy', sorting[0].id);
        searchParams.set('sortOrder', sorting[0].desc ? 'DESC' : 'ASC');
      } else {
        searchParams.delete('sortBy');
        searchParams.delete('sortOrder');
      }
      
      const newSearch = searchParams.toString();
      if (newSearch !== location.search.substring(1)) {
        navigate({
          pathname: location.pathname,
          search: newSearch
        }, { replace: true });
      }
    }, 300); // Debounce URL updates

    return () => clearTimeout(timeoutId);
  }, [pagination, globalFilter, sorting, navigate, location.pathname, location.search]);

  // Handle pagination change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, pageIndex: newPage }));
  };

  // Handle entries per page change
  const handleEntriesPerPageChange = (e) => {
    const pageSize = Number(e.target.value);
    setPagination(prev => ({ ...prev, pageIndex: 0, pageSize }));
  };

  // Build pagination controls
  const totalPages = Math.ceil(rowCount / pagination.pageSize);
  const currentPage = pagination.pageIndex + 1;

  const paginationControls = (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mb-6">
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Items per page:</span>
          <select
            value={pagination.pageSize}
            onChange={handleEntriesPerPageChange}
            className="block w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={40}>40</option>
          <option value={60}>60</option>
          <option value={100}>100</option>
        </select>
        <span className="text-sm text-gray-600">entries per page</span>
      </div>

      <span className="flex items-center gap-1 text-sm text-gray-600">
        Showing {Math.min(rowCount, pagination.pageIndex * pagination.pageSize + 1)} to {Math.min(rowCount, (pagination.pageIndex + 1) * pagination.pageSize)} of {rowCount} entries
      </span>

      <div className="flex items-center gap-2">
        <button
          onClick={() => handlePageChange(0)}
          disabled={currentPage === 1}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => handlePageChange(pagination.pageIndex - 1)}
          disabled={currentPage === 1}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <span className="flex items-center gap-1 text-sm text-gray-600 px-4">
          Page{' '}
          <strong>
            {pagination.pageIndex + 1} of{' '}{totalPages}
          </strong>
        </span>
        
        <button
          onClick={() => handlePageChange(pagination.pageIndex + 1)}
          disabled={currentPage === totalPages}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          onClick={() => handlePageChange(totalPages - 1)}
          disabled={currentPage === totalPages}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
    </div>
  );

  // Action handlers
  const handleAdd = () => {
    setSelectedAsset(null);
    setShowAddModal(true);
  };

  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    setShowEditModal(true);
  };

  const handleView = (asset) => {
    setSelectedAsset(asset);
    setShowViewModal(true);
  };

  const handleAllocate = (asset) => {
    setSelectedAsset(asset);
    setShowAllocateModal(true);
  };

  const handleTag = (asset) => {
    setSelectedAsset(asset);
    setShowTagModal(true);
  };

  const handleDelete = async (asset) => {
    if (window.confirm(`Are you sure you want to delete asset "${asset.asset_name}"?`)) {
      try {
        await deleteAsset({
          asset_id: asset.asset_id,
          name: asset.asset_name,
          asset_disposed_details: 'Deleted via web interface',
          asset_disposal_id: 1 // Default disposal reason
        });
        toast.success('Asset deleted successfully');
        fetchAssets();
      } catch (error) {
        toast.error('Failed to delete asset');
      }
    }
  };

  const handleImport = () => {
    setShowImportModal(true);
  };

  // Modal success handlers
  const handleAssetCreated = () => {
    setShowAddModal(false);
    fetchAssets();
    toast.success('Asset created successfully');
  };

  const handleAssetUpdated = () => {
    setShowEditModal(false);
    fetchAssets();
    toast.success('Asset updated successfully');
  };

  const handleAssetAllocated = () => {
    setShowAllocateModal(false);
    fetchAssets();
    toast.success('Asset allocated successfully');
  };

  const handleAssetsImported = () => {
    setShowImportModal(false);
    fetchAssets();
    toast.success('Assets imported successfully');
  };

  const handleResendDeclarationEmail = async (asset) => {
    const assetId = asset.asset_id;
    setResendingEmail(prev => ({ ...prev, [assetId]: true }));
    
    try {
      // Get allocation details - we need the allocation ID and user ID
      const userId = asset.user_id || asset.allocate_staff_id;
      const allocationId = asset.allocation_id || asset.allocate_id;
      
      if (!userId) {
        toast.error('Cannot resend email: User information not found');
        return;
      }

      await sendDeclarationEmail({
        ticket_id: 999999, // Pseudo ticket ID for standalone declarations
        user_id: userId,
        asset_ids: [assetId]
      });
      
      toast.success('Declaration email sent successfully');
    } catch (error) {
      console.error('Error resending declaration email:', error);
      toast.error('Failed to send declaration email');
    } finally {
      setResendingEmail(prev => ({ ...prev, [assetId]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header Section */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="flex justify-between items-start">
            {/* Left: Logo, Title and Results */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  Hardware Assets
                </h1>
                <p className="text-xs text-gray-500 mb-1">
                  Physical IT equipment and devices
                </p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {rowCount ? `Showing ${Math.min(pagination.pageSize, rowCount)} of ${rowCount} results` : '0 results'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Actions */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                {can("Manage Assets") && (
                  <>
                    <button
                      onClick={handleImport}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      Import Assets
                    </button>
                    <button
                      onClick={handleAdd}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1"
                      style={{ backgroundColor: '#0087D4', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)', borderColor: '#0087D4' }}
                    >
                      <svg className="-ml-0.5 mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Add Hardware
                    </button>
                  </>
                )}
                
                {/* Sort By Dropdown */}
                <select
                  value={sorting.length > 0 ? `${sorting[0].id}-${sorting[0].desc ? 'desc' : 'asc'}` : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      const [columnId, order] = value.split('-');
                      setSorting([{ id: columnId, desc: order === 'desc' }]);
                    } else {
                      setSorting([]);
                    }
                  }}
                  className="block w-40 px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Sort by...</option>
                  <option value="asset_name-asc">Name (A-Z)</option>
                  <option value="asset_name-desc">Name (Z-A)</option>
                  <option value="asset_tag-asc">Tag (A-Z)</option>
                  <option value="asset_tag-desc">Tag (Z-A)</option>
                  <option value="vendor-asc">Vendor (A-Z)</option>
                  <option value="vendor-desc">Vendor (Z-A)</option>
                  <option value="asset_cost-desc">Cost (High-Low)</option>
                  <option value="asset_cost-asc">Cost (Low-High)</option>
                  <option value="asset_date_purchased-desc">Date (Newest)</option>
                  <option value="asset_date_purchased-asc">Date (Oldest)</option>
                  <option value="state-asc">Status (A-Z)</option>
                  <option value="state-desc">Status (Z-A)</option>
                </select>
              </div>
              
              {/* Search and View Toggle Controls */}
              <div className="flex gap-2 w-full">
                {/* View Mode Toggle */}
                <div className="flex-shrink-0 inline-flex rounded-md shadow-sm border border-gray-300 bg-white" role="group">
                  <button
                    onClick={() => handleViewModeChange('grid')}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-l-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-blue-50 text-blue-700 border-r border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50 border-r border-gray-300'
                    }`}
                    title="Grid View"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleViewModeChange('table')}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-r-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
                      viewMode === 'table'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Table View"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                </div>
                
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <DebouncedInput
                    value={globalFilter ?? ''}
                    onChange={handleSearch}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Search hardware assets..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Reduce spacer */}
      <div className="mt-1"></div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4 mt-1">
      
      {/* Hardware Assets Grid */}
      {loading ? (
        <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
          <div className="flex justify-center items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading hardware assets...</span>
          </div>
        </div>
      ) : assets.length === 0 ? (
        <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
          <p className="text-gray-500">No hardware assets found.</p>
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? (
            /* Grid View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
              {assets.map((asset) => (
            <div key={asset.asset_id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow hover:border-blue-200 transition-all duration-200 group flex flex-col h-full">
              {/* Header with Asset Tag and Category */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                    {asset.asset_tag}
                  </span>
                  {asset.category && (
                    <span className="text-xs font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                      {asset.category}
                    </span>
                  )}
                  {asset.allocate_declare == 1 && !asset.allocate_declared && (
                    <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800" title="User has not yet confirmed receipt">
                      Declaration pending
                    </span>
                  )}
                </div>
                {(() => {
                  const displayStatus = asset.state || 'Unknown';
                  const statusClass = 
                    displayStatus === 'New' ? 'bg-green-100 text-green-800' :
                    displayStatus === 'Good' || displayStatus === 'Working' ? 'bg-green-100 text-green-800' :
                    displayStatus === 'Fair' ? 'bg-yellow-100 text-yellow-800' :
                    displayStatus === 'Retired' ? 'bg-orange-100 text-orange-800' :
                    displayStatus === 'Scrapped' || displayStatus === 'Broken' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800';
                  
                  return (
                    <div className="flex items-center gap-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusClass}`}>
                        {displayStatus}
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Title */}
              <h3 className="font-semibold text-sm text-gray-900 mb-2 line-clamp-2 min-h-[2rem]">
                {asset.asset_name}
              </h3>

              {/* Flexible content area */}
              <div className="flex-1 mb-2">
                {/* Compact Metadata */}
                <div className="space-y-0.5 text-xs text-gray-600">
                  {asset.asset_serial && (
                    <div className="flex items-center">
                      <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                      </svg>
                      <span className="truncate font-mono text-[11px]">{asset.asset_serial}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center">
                    <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-2-5h6m-6 0V9m0 0h6m-6 0V5a2 2 0 012-2h4a2 2 0 012 2v4" />
                    </svg>
                    <span className="truncate font-medium">{asset.vendor || 'Unknown Vendor'}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="truncate">{asset.user_full_name || 'Unassigned'}</span>
                  </div>

                  <div className="flex items-center">
                    <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    <span>
                      {asset.asset_cost ? `R ${parseFloat(asset.asset_cost).toLocaleString()}` : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-1 pt-2 border-t border-gray-100 mt-auto">
                <button
                  onClick={() => handleView(asset)}
                  className="flex-1 inline-flex items-center justify-center px-2 py-1.5 text-white text-xs font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                  style={{ backgroundColor: '#0087D4', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' }}
                >
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                  </svg>
                  View
                </button>
                
                {can("Manage Assets") && (
                  <>
                    <button
                      onClick={() => handleEdit(asset)}
                      className="inline-flex items-center justify-center px-2 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
                      title="Edit"
                    >
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                      </svg>
                      Edit
                    </button>
                    
                    <button
                      onClick={() => handleTag(asset)}
                      className="inline-flex items-center justify-center px-2 py-1.5 border border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
                      title="Generate Asset Tag"
                    >
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM12 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM12 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-3z" clipRule="evenodd" />
                      </svg>
                      Tag
                    </button>
                  </>
                )}
              </div>
            </div>
              ))}
            </div>
          ) : (
            /* Table View */
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Asset Tag
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendor
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Condition
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cost
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {assets.map((asset) => {
                      const displayStatus = asset.state || 'Unknown';
                      const statusClass = 
                        displayStatus === 'New' ? 'bg-green-100 text-green-800' :
                        displayStatus === 'Good' || displayStatus === 'Working' ? 'bg-green-100 text-green-800' :
                        displayStatus === 'Fair' ? 'bg-yellow-100 text-yellow-800' :
                        displayStatus === 'Retired' ? 'bg-orange-100 text-orange-800' :
                        displayStatus === 'Scrapped' || displayStatus === 'Broken' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800';
                      
                      return (
                        <tr key={asset.asset_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div>
                              <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                {asset.asset_tag}
                              </span>
                              {asset.allocate_declare == 1 && !asset.allocate_declared && (
                                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800" title="User has not yet confirmed receipt">
                                  Pending
                                </span>
                              )}
                            </div>
                            {asset.asset_serial && (
                              <div className="text-xs text-gray-500 font-mono mt-1">
                                {asset.asset_serial}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                {asset.asset_name}
                              </div>
                              {asset.user_full_name && (
                                <div className="text-xs text-gray-500 mt-1">
                                  <svg className="w-3 h-3 inline-block mr-1 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                  {asset.user_full_name}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {asset.category && (
                              <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded">
                                {asset.category}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {asset.vendor || '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${statusClass}`}>
                              {displayStatus}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {asset.asset_cost ? `R ${parseFloat(asset.asset_cost).toFixed(2)}` : '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleView(asset)}
                                className="inline-flex items-center px-2 py-1 text-white text-xs font-medium rounded shadow-sm hover:opacity-90"
                                style={{ backgroundColor: '#0087D4' }}
                                title="View"
                              >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                                </svg>
                              </button>
                              
                              {can("Manage Assets") && (
                                <>
                                  <button
                                    onClick={() => handleEdit(asset)}
                                    className="inline-flex items-center px-2 py-1 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded shadow-sm"
                                    title="Edit"
                                  >
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                                    </svg>
                                  </button>
                                  
                                  <button
                                    onClick={() => handleTag(asset)}
                                    className="inline-flex items-center px-2 py-1 border border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-medium rounded shadow-sm"
                                    title="Generate Asset Tag"
                                  >
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM12 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM12 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-3z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {paginationControls}
    </div>
    
    {/* Modals */}
    {showAddModal && (
      <AddHardwareModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAssetCreated}
      />
    )}

    {showEditModal && selectedAsset && (
      <EditHardwareModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleAssetUpdated}
        asset={selectedAsset}
      />
    )}

    {showViewModal && selectedAsset && (
      <ViewHardwareModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        asset={selectedAsset}
        onSuccess={() => {
          fetchAssets(); // Refresh the assets list
          setShowViewModal(false); // Close the modal
        }}
      />
    )}

    {showAllocateModal && selectedAsset && (
      <AllocateAssetModal
        isOpen={showAllocateModal}
        onClose={() => setShowAllocateModal(false)}
        onSuccess={handleAssetAllocated}
        asset={selectedAsset}
      />
    )}

    {showImportModal && (
      <ImportAssetsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={handleAssetsImported}
      />
    )}

    {showTagModal && selectedAsset && (
      <AssetTagModal
        isOpen={showTagModal}
        onClose={() => setShowTagModal(false)}
        asset={selectedAsset}
        platformName="Silulumanzi"
        contactNo="+27 123 456 789"
      />
    )}
    </div>
  );
}

export default HardwareAssets;
